import { useState } from 'react';
import { Box, Typography, Chip, IconButton, Avatar, Stack } from '@mui/joy';
import CreditCardRoundedIcon      from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon        from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon     from '@mui/icons-material/PointOfSaleRounded';
import ChatBubbleOutlineRounded   from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckRoundedIcon           from '@mui/icons-material/CheckRounded';
import DeleteOutlineRoundedIcon   from '@mui/icons-material/DeleteOutlineRounded';

const MONEY_META = {
    transfer: { label: 'Перевод',  color: '#818cf8', bg: 'rgba(129,140,248,0.14)' },
    cash:     { label: 'Наличные', color: '#22c55e', bg: 'rgba(34,197,94,0.14)'   },
    terminal: { label: 'Терминал', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)'  },
};
const fmtDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—');
const fmtMoney = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/**
 * Компактная СТРОКА чека для рабочего аккаунта (история чеков + статистика).
 * onConfirm(order) — подтверждение (для неподтверждённых).
 * onDelete(order)  — удаление/отклонение (родитель решает: заявка или отмена фантома).
 */
export default function CompactCheckCard({ group, onConfirm, onDelete, busy = false }) {
    const [commentOpen, setCommentOpen] = useState(false);
    if (!group || group.length === 0) return null;

    const head         = group[0];
    const groupTotal   = group.reduce((s, o) => s + parseFloat(o.value || 0), 0);
    const groupComment = group.map(o => o.comment).find(c => c && c.trim()) || '';
    const isPending    = group.some(o => o.confirmation_status === 'pending');
    const moneyTypes   = [...new Set(group.map(o => o.money_type))];

    const metaLine = [
        head.location_name,
        head.administrator_name ? `Админ: ${head.administrator_name}` : null,
    ].filter(Boolean).join(' · ');

    return (
        <Box sx={{
            borderRadius: 'var(--joy-radius-md)',
            border: '1px solid var(--joy-palette-divider)',
            borderLeft: `3px solid ${isPending ? '#f59e0b' : '#22c55e'}`,
            bgcolor: 'var(--joy-palette-background-level1)',
            px: 1, py: 0.8,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Аватар */}
                <Avatar src={head.photographer_avatar_url} size="sm" sx={{ bgcolor: 'var(--joy-palette-primary-500)', flexShrink: 0 }}>
                    {(head.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>

                {/* Имя + точка·админ + дата/время (левый нижний угол) */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>
                        {head.photographer_name}
                    </Typography>
                    {metaLine && (
                        <Stack direction={'column'}>
                            <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)' }} noWrap>
                                {head.location_name}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)' }} noWrap>
                                {head.administrator_name && (
                                    <>Админ: <Box component="span" sx={{ color: '#06b6d4', fontWeight: 700 }}>{head.administrator_name}</Box></>
                                )}
                            </Typography>
                        </Stack>
                    )}
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }} noWrap>
                        {isPending
                            ? <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}>ожидает · {fmtDateTime(head.date_create)}</Box>
                            : fmtDateTime(head.date_create)}
                    </Typography>
                </Box>

                <Stack direction={'column'}>
                    {/* Сумма */}
                    <Typography sx={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', minWidth: 56, textAlign: 'right' }}>
                        {fmtMoney(groupTotal)}
                    </Typography>

                    {/* Тип оплаты */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, alignItems: 'flex-end', flexShrink: 0 }}>
                        {moneyTypes.map(mt => {
                            const meta = MONEY_META[mt] || MONEY_META.transfer;
                            return (
                                <Chip key={mt} size="sm" sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 10, fontWeight: 700, '--Chip-minHeight': '20px' }}>
                                    {meta.label}
                                </Chip>
                            );
                        })}
                    </Box>
                </Stack>

                {/* Комментарий (иконка, если есть) */}
                {groupComment && (
                    <IconButton size="sm" variant="soft" color="neutral"
                        onClick={() => setCommentOpen(v => !v)}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <ChatBubbleOutlineRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                {/* Подтвердить (для неподтверждённых) */}
                {isPending && onConfirm && (
                    <IconButton size="sm" variant="solid" color="success"
                        onClick={() => group.forEach(o => o.confirmation_status === 'pending' && onConfirm(o))}
                        disabled={busy}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <CheckRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                {/* Удалить */}
                {onDelete && (
                    <IconButton size="sm" variant="outlined" color="danger"
                        onClick={() => onDelete(head)} disabled={busy}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </Box>

            {/* Раскрытый комментарий */}
            {groupComment && commentOpen && (
                <Box sx={{ mt: 0.6, px: 1, py: 0.5, borderRadius: 'var(--joy-radius-sm)', bgcolor: 'var(--joy-palette-background-level2)' }}>
                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {groupComment}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}